import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetchMarkers, addMarker, deleteMarker, Marker } from './api';
import { filterByTags, normalizeFilters } from './filterActivities';
import useEvent from '../../event';
import { Activity, useActivities } from '../../activities';
import useEventSettings from '../../eventSettings';
import useUserProfile from '../useUserProfile';

const createMarker = (activityId: number, eventId: number, userProfileId: number) => ({
    eventId,
    userProfileId,
    markerType: 'addToCal' as const,
    entity: 'sessions' as const,
    entityId: activityId,
});

type TagGroupType = Record<string, number[]>;

type Options = {
    filters: {
        tags?: TagGroupType;
    };
    limit: number;
    offset?: number;
};

export const useFavoriteActivitiesIds = (eventUniqueName: string) => {
    const { data: event, isLoading: isLoadingEvent } = useEvent(eventUniqueName);
    const { data: markers, isLoading: isLoadingMarkers } = useQuery('markers', () =>
        fetchMarkers()
    );

    if (isLoadingEvent || isLoadingMarkers) {
        return { isFavoriteActivity: null, isLoading: isLoadingEvent || isLoadingMarkers };
    }
    const isFavoriteActivity = ({ eventId, markerType, entity }) =>
        entity === 'sessions' && eventId === event?.id && markerType === 'addToCal';

    return {
        data: markers?.filter(isFavoriteActivity).map(({ entityId }) => entityId),
        isLoading: false,
    };
};

export const useFavorites = (eventUniqueName: string): Activity[] => {
    const { data: event } = useEvent(eventUniqueName);
    const { data: activities } = useActivities(eventUniqueName);
    const { data: favoriteActivityIds } = useFavoriteActivitiesIds(eventUniqueName);

    return useMemo(() => {
        if (!event || !activities || !favoriteActivityIds) {
            return [];
        }

        const favoriteActivities = activities.filter(({ id }) => favoriteActivityIds.includes(id));

        return favoriteActivities;
    }, [favoriteActivityIds, activities, event]);
};

export const usePaginatedFavorites = (
    eventUniqueName: string,
    { offset = 0, filters = {}, limit = 0 }: Options
) => {
    const { data: settings, isLoading } = useEventSettings(eventUniqueName);
    const favoriteActivities = useFavorites(eventUniqueName);
    let groups = {
        favorites: [],
        total: 0,
        offset: 0,
        isLoading,
    };

    return useMemo(() => {
        if (!favoriteActivities && isLoading) {
            return groups;
        }
        let filteredActivities = favoriteActivities;
        if (filters?.tags && settings?.filters?.length) {
            const normalizedFilters = normalizeFilters(filters, settings.filters);
            filteredActivities = filterByTags(favoriteActivities, normalizedFilters.tags);
        }

        const start = Math.min(offset, filteredActivities.length);
        const end = limit
            ? Math.min(offset + limit, filteredActivities.length)
            : filteredActivities.length;

        groups = {
            favorites: filteredActivities.slice(start, end),
            total: filteredActivities.length,
            offset: start,
            isLoading,
        };

        return groups;
    }, [favoriteActivities, settings, isLoading, filters]);
};

export const useIsActivityFavorite = (eventUniqueName: string, activityId: number): boolean => {
    const favorites = useFavorites(eventUniqueName);

    return !!favorites.find(({ id }) => id === activityId);
};

export const useAddToFavorites = (eventUniqueName: string) => {
    const queryClient = useQueryClient();
    const { data: userProfile } = useUserProfile();
    const { data: event } = useEvent(eventUniqueName);

    const { mutate: addToFavorites } = useMutation(
        async (activityId: number) => {
            const marker = createMarker(activityId, event.id, userProfile.id);
            return addMarker(marker);
        },
        {
            onMutate: async (activityId: number) => {
                await queryClient.cancelQueries('markers');
                const previousMarkers = queryClient.getQueryData<Marker[]>('markers');
                queryClient.setQueryData<Marker[]>('markers', old => [
                    ...old,
                    createMarker(activityId, event.id, userProfile.id),
                ]);
                return { previousMarkers };
            },
            onError: (_error, _activityId, context) => {
                queryClient.setQueryData('markers', context.previousMarkers);
            },
            onSettled: () => {
                queryClient.invalidateQueries('markers');
            },
        }
    );

    return useCallback(addToFavorites, []);
};

export const useRemoveFromFavorites = (eventUniqueName: string) => {
    const queryClient = useQueryClient();
    const { data: userProfile } = useUserProfile();
    const { data: event } = useEvent(eventUniqueName);

    const { mutate: removeFromFavorites } = useMutation(
        async (activityId: number) => {
            const marker = createMarker(activityId, event.id, userProfile.id);
            return deleteMarker(marker);
        },
        {
            onMutate: async (activityId: number) => {
                await queryClient.cancelQueries('markers');
                const previousMarkers = queryClient.getQueryData<Marker[]>('markers');
                queryClient.setQueryData<Marker[]>('markers', old =>
                    old.filter(({ entityId }) => entityId !== activityId)
                );
                return { previousMarkers };
            },
            onError: (_error, _activityId, context) => {
                queryClient.setQueryData('markers', context.previousMarkers);
            },
            onSettled: () => {
                queryClient.invalidateQueries('markers');
            },
        }
    );

    return useCallback(removeFromFavorites, []);
};
